import React from "react";
import { Grid, TextField, MenuItem } from "@mui/material";
import { Button, ButtonProps, Stack, Typography } from "@financeable-com-au/financeable-ui";
import { FILE_AWAITING_MESSAGE, PPSR_CERTIFICATE_DOCUMENT_NAME, StateList, VEHICLE_LOOKUP_SEARCH } from "@/constants";
import { SearchVehicleProps } from "@/utils/interface";
import { Document } from "@/store/slices/types/applicationFormSlice";
import { getAssetFieldAnchorId } from "@/utils/commonFunctions";
import { ASSET_FIELD } from "./assetRequiredFields";
import FormInputField from "../CommonComponents/FormInputField";

/** The two lookups differ only in their label, emphasis, gate and payload — everything else is shared. */
interface Lookup {
  id: string;
  label: string;
  variant: ButtonProps["variant"];
  isOffered: boolean;
  disabled: boolean;
  onClick: () => void;
}

/**
 * Identify the vehicle by the plate on it or the VIN stamped into it. Rendered inside an asset search
 * option, which owns the heading and decides whether this option is offered at all.
 *
 * Whichever provider serves the tenant answers both buttons — the server dispatches on the tenant's
 * lookup provider. `servicePermission.blug_flag` / `blug_flag_ppsr` are per-user permissions rather
 * than Blueflag entitlements, so they remain the cost gate for either provider.
 */
const SearchVehicle = ({
  asset,
  allValues,
  servicePermission,
  canUsePaidServices,
  fieldErrors,
  getLocalAndLenderError,
  handle,
  isVehicleSearch,
  isPPSRSearch,
  isDisableEdit,
}: SearchVehicleProps) => {
  const { _id: assetId, searchVehicleLoader = false, searchPPSRLoader = false } = asset;

  /** Anchor ids let the "Requires ..." hints scroll straight to the field that is still empty. */
  const anchorId = (fieldName: string) => getAssetFieldAnchorId(fieldName, assetId) || undefined;

  // A VIN identifies a vehicle on its own; a plate only does so together with the state that issued it.
  const hasIdentifier = Boolean(allValues?.vin || (allValues?.registrationNumber && allValues?.registrationState));
  const cannotLookUp = !canUsePaidServices || !hasIdentifier || isDisableEdit;

  let lookupRequirementMessage = "";

  const plate = allValues?.registrationNumber?.trim();
  const vin = allValues?.vin?.trim();

  if (!plate && !vin) {
    lookupRequirementMessage = VEHICLE_LOOKUP_SEARCH.REQUIRES_IDENTIFIER;
  } else if (plate && !allValues?.registrationState) {
    lookupRequirementMessage = VEHICLE_LOOKUP_SEARCH.REQUIRES_STATE;
  }

  /** Both lookups are keyed the same way: by plate when there is one, otherwise by VIN. */
  const lookupQuery = () =>
    allValues?.registrationNumber
      ? { registrationNumber: allValues?.registrationNumber, registrationState: allValues?.registrationState }
      : { vin: allValues?.vin };

  const hasPPSRCertificate =
    (asset?.documents?.filter((doc: Document) => doc.name === PPSR_CERTIFICATE_DOCUMENT_NAME)?.length ?? 0) > 0;

  const isFieldDisabled = !canUsePaidServices || searchVehicleLoader || isDisableEdit;

  // The required-field rule stores this group under its own key, not any individual input's key.
  const identifierRequirementError =
    vin || (plate && allValues?.registrationState?.trim())
      ? undefined
      : getLocalAndLenderError({ fieldName: "registrationNumberOrVin" });
  const registrationNumberError =
    getLocalAndLenderError({
      fieldName: "registrationNumber",
      fieldError: fieldErrors?.registrationNumberOrVin,
    }) || identifierRequirementError;
  const registrationStateError =
    getLocalAndLenderError({
      fieldName: "registrationState",
      fieldError: fieldErrors?.registrationState,
    }) || identifierRequirementError;
  const vinError =
    getLocalAndLenderError({
      fieldName: "vin",
      fieldError: fieldErrors?.registrationNumberOrVin,
    }) || identifierRequirementError;

  const lookups: Lookup[] = [
    {
      id: `vehicle-lookup-${assetId}`,
      label: VEHICLE_LOOKUP_SEARCH.VEHICLE_BUTTON,
      variant: "primary",
      isOffered: Boolean(isVehicleSearch && servicePermission?.blug_flag),
      disabled: cannotLookUp || searchVehicleLoader,
      onClick: () => handle.getVehicleLookup(lookupQuery()),
    },
    {
      id: `ppsr-lookup-${assetId}`,
      label: VEHICLE_LOOKUP_SEARCH.PPSR_BUTTON,
      variant: "secondary",
      isOffered: Boolean(isPPSRSearch && servicePermission?.blug_flag_ppsr),
      disabled: cannotLookUp || searchPPSRLoader,
      onClick: () => handle.getVehiclePPSRSearch(lookupQuery()),
    },
  ];
  const offeredLookups = lookups.filter((lookup) => lookup.isOffered);

  // One note serves both buttons: what a running lookup is waiting on, otherwise what they both still need.
  const lookupNote =
    (searchVehicleLoader && FILE_AWAITING_MESSAGE.gettingVehicleDataMsg) ||
    (searchPPSRLoader && !hasPPSRCertificate && FILE_AWAITING_MESSAGE.gettingPPSRFileMsg) ||
    (!hasIdentifier && lookupRequirementMessage) ||
    undefined;

  // A disabled button is out of the tab order, so the reason it is disabled has to reach a screen
  // reader through the button's description rather than by landing on the text next to it.
  const noteId = lookupNote ? `vehicle-lookup-note-${assetId}` : undefined;

  return (
    // `useFlexGap` keeps the Stack off its children's margins, which the Grid's spacing relies on.
    <Stack spacing={3} useFlexGap>
      <Grid container spacing={1}>
        {isVehicleSearch && (
          <>
            <FormInputField
              type="text"
              id={anchorId(ASSET_FIELD.registrationNumber)}
              fieldName="registrationNumber"
              label="Registration no."
              placeholder="Type Registration"
              value={allValues?.registrationNumber}
              error={!!registrationNumberError}
              helperText={registrationNumberError}
              disabled={isFieldDisabled}
              onChange={handle.onChangeField}
              onBlur={(_fieldName: string, value: string) => {
                handle.updateAsset({ registrationNumber: value });
              }}
              gridProps={{ xs: 8, md: 5 }}
            />

            <Grid item xs={4} md={2}>
              <TextField
                fullWidth
                variant="outlined"
                select
                id={anchorId(ASSET_FIELD.registrationState)}
                disabled={isFieldDisabled}
                value={allValues?.registrationState}
                name="registrationState"
                label="Rego. state"
                error={!!registrationStateError}
                helperText={registrationStateError}
                size="small"
                onChange={(event) => {
                  handle.onChangeField(event?.target?.value, "registrationState");
                  handle.updateAsset({ registrationState: event.target.value });
                }}
              >
                {StateList.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </>
        )}

        {isPPSRSearch && (
          <FormInputField
            id={anchorId(ASSET_FIELD.vin)}
            fieldName="vin"
            label="VIN"
            type="text"
            value={allValues?.vin}
            error={!!vinError}
            helperText={vinError}
            disabled={isFieldDisabled}
            onChange={handle.onChangeField}
            onBlur={(_fieldName: string, value: string) => {
              if (value.length >= 10) {
                handle.updateAsset({ vin: value });
              }
            }}
            gridProps={{ xs: 12, md: 5 }}
          />
        )}
      </Grid>

      {offeredLookups.length > 0 && (
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          {offeredLookups.map(({ id, label, variant, disabled, onClick }) => (
            <Button key={id} id={id} variant={variant} disabled={disabled} onClick={onClick} aria-describedby={noteId}>
              {label}
            </Button>
          ))}
          {lookupNote && (
            <Typography id={noteId} level="body-xs" textColor="text.tertiary">
              {lookupNote}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default SearchVehicle;
